import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { PropertyUnitPicker, usePropertyUnitCascade } from "@/components/PropertyUnitPicker";
import { Button } from "@/components/ui/Button";
import { Combobox } from "@/components/ui/Combobox";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type { LeaseResource, Page, TenantResource } from "@/lib/api-types";
import { LEASE_STATUS_LABELS } from "@/lib/enum-labels";
import { labelFor } from "@/lib/field-labels";
import { formatDateCell } from "@/lib/format";

/**
 * The real "Add lease" creation flow (Story 15.3) — `POST /leases`/`POST
 * /leases/{id}/tenants` were both fully built, tested and role-gated on the
 * backend since Story 6.3/6.5, but nothing in the frontend ever called
 * either one: an Owner could import leases in bulk but never add a single
 * new one through the app itself (`epics.md`'s own 2026-08-23/24 backlog
 * item 3).
 *
 * A single Dialog-hosted form, not a multi-step wizard (spec Intent) —
 * lease creation has exactly one real write-ordering dependency (co-tenants
 * can only attach to a lease that already exists), not the genuine
 * sequential-dependent-writes chain `AddPropertyWizard.tsx` has (property
 * -> units -> equipment, nothing written until Finish). Mirrors `AddTenant
 * Dialog.tsx`'s/`BulkEditEquipmentDialog.tsx`'s own simpler shape: one
 * submit, then a real result view.
 *
 * **No Unit record page exists anywhere in this codebase** (spec Intent) —
 * the unit picker here is a cascading Property -> Unit selection, since
 * units are only listable scoped under a property (`GET /properties/
 * {prop_id}/units`, no top-level unit search route). That same route
 * already excludes the synthetic sentinel unit every property carries
 * (AD-18) at the repo layer (`units_repo.list_units_for_property`'s own
 * `include_sentinel=False` default, `app/api/units.py`'s own module
 * docstring) — this picker renders exactly what the route returns, so it
 * never has to filter a sentinel back out itself, and never offers a
 * choice the backend's own `409 sentinel_unit` would refuse anyway.
 *
 * Judgement call 2 (spec): the optional fields exposed here are the AC's
 * own named minimum (`status`, `end_date`, `monthly_rent_cents`,
 * `lease_type`) plus `notice_period_days` — Story 6.6's own "never null,
 * defaults sensibly" design (`compute_notify_by` degrades a missing value
 * safely) means leaving it blank here is always safe, and it is genuinely
 * likely to be known and worth setting at the moment of creating a lease,
 * unlike the deposit/NJ-notice/escalation fields this dialog deliberately
 * defers to the lease record page's own existing inline-edit affordances.
 *
 * Judgement call 3 (spec): tenant-attach outcomes are reported per-tenant
 * *row*, by name, in the dialog's own result view (mirrors `AddProperty
 * Wizard.tsx`'s own per-unit outcome list) — never a single pass/fail toast
 * that cannot represent "the lease was created, two tenants attached, one
 * didn't." This dialog deliberately never pre-filters or deduplicates a
 * row's own tenant selection against another row's (mirrors AD-20's own
 * whole-codebase discipline: never a racy, opinionated client-side guess
 * about what the backend will accept — only the real, authoritative
 * refusal once actually attempted, exactly `create_lease`/`create_unit`'s
 * own "insert first, translate the genuine conflict" shape). Picking the
 * same tenant in two rows is therefore a real, legal thing to do here: it
 * fires two real, separate `POST /leases/{id}/tenants` calls, and the
 * backend's own natural-key guard on the attempt (`409 duplicate_co_
 * tenant` — confirmed against `app/api/leases.py`: there is no "already on
 * another lease for this unit" rule anywhere in this backend, a tenant may
 * legitimately hold several concurrent leases) is what actually refuses
 * the second one, honestly reported on its own row rather than silently
 * collapsed into a single call the Owner never explicitly asked for.
 */

const CREATABLE_STATUSES = ["draft", "active"] as const;
const DEFAULT_STATUS = "draft";

interface TenantOutcome {
  //: A stable React key for this one outcome row — never just `tenantId`,
  //: since two rows may legitimately name the same tenant (this file's own
  //: doc comment above `tenantOptions`) and would otherwise collide.
  key: string;
  tenantId: string;
  label: string;
  status: "attached" | "failed";
  message?: string;
}

interface CreatedResult {
  lease: LeaseResource;
  tenantOutcomes: TenantOutcome[];
}

export function AddLeaseDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  //: Cascading (judgement call 1) — disabled/empty until a property is
  //: chosen, mirroring `AddPropertyWizard.tsx`'s own dependent-selection
  //: shape for the municipality step. Never returns the sentinel unit (this
  //: file's own doc comment above). Extracted into `PropertyUnitPicker.tsx`
  //: (Story 15.9, judgement call 4) once `AddTenantDialog.tsx` needed the
  //: identical cascade a second time.
  const cascade = usePropertyUnitCascade(open);
  const [startDate, setStartDate] = useState("");
  const [status, setStatus] = useState<string>(DEFAULT_STATUS);
  const [endDate, setEndDate] = useState("");
  const [monthlyRentDollars, setMonthlyRentDollars] = useState("");
  const [leaseType, setLeaseType] = useState("");
  const [noticePeriodDays, setNoticePeriodDays] = useState("");
  //: Zero or more selected tenant ids (spec AC: "zero is a valid, supported
  //: choice") — `null` marks a row whose own combobox has not picked a
  //: tenant yet.
  const [tenantSelections, setTenantSelections] = useState<(string | null)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<CreatedResult | null>(null);

  const tenantsQuery = useQuery({
    queryKey: ["tenants", "list", "for-add-lease-dialog"] as const,
    queryFn: () => apiFetch<Page<TenantResource>>("/tenants"),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      cascade.reset();
      setStartDate("");
      setStatus(DEFAULT_STATUS);
      setEndDate("");
      setMonthlyRentDollars("");
      setLeaseType("");
      setNoticePeriodDays("");
      setTenantSelections([]);
      setSubmitError(undefined);
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  //: Every row offers the full tenant roster, unfiltered — this dialog
  //: never pre-checks or deduplicates a selection against the others
  //: client-side (mirrors AD-20's own discipline, established everywhere
  //: else in this codebase: never a racy/opinionated client-side guess
  //: about what the backend will accept, only the real, authoritative
  //: refusal once attempted). Picking the same tenant twice is a real,
  //: legal thing to do in this form — it fires two real, separate `POST
  //: /leases/{id}/tenants` calls (one per row, VC-15.3-06's own "one call
  //: per selected tenant"), and the backend's own natural-key guard on the
  //: second one is what actually refuses it, honestly reported per-row
  //: rather than silently collapsed into one call the Owner never asked
  //: for.
  const tenantOptions = (tenantsQuery.data?.data ?? []).map((tenant) => ({
    value: tenant.id,
    label: tenant.full_name,
  }));
  const tenantLabelById = new Map(tenantOptions.map((option) => [option.value, option.label]));

  const canSubmit = cascade.unitId !== "" && startDate.trim() !== "" && !isSubmitting;

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(undefined);
    try {
      const lease = await apiFetch<LeaseResource>("/leases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unit_id: cascade.unitId,
          start_date: startDate,
          status,
          end_date: endDate === "" ? null : endDate,
          monthly_rent_cents:
            monthlyRentDollars.trim() === "" ? null : Math.round(Number(monthlyRentDollars) * 100),
          lease_type: leaseType.trim() === "" ? null : leaseType.trim(),
          notice_period_days: noticePeriodDays.trim() === "" ? null : Number(noticePeriodDays),
        }),
      });

      // Judgement call 3 — one real, separate POST per selected tenant row,
      // after the lease itself exists; each outcome reported individually,
      // never rolled up into a single pass/fail (AC), and never silently
      // collapsed even when two rows name the same tenant (this file's own
      // doc comment above `tenantOptions`).
      const selectedTenantIds = tenantSelections.filter((value): value is string => value !== null);
      const tenantOutcomes: TenantOutcome[] = [];
      for (const [index, tenantId] of selectedTenantIds.entries()) {
        const label = tenantLabelById.get(tenantId) ?? tenantId;
        const outcomeKey = `${tenantId}-${index}`;
        try {
          await apiFetch<LeaseResource>(`/leases/${lease.id}/tenants`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tenant_id: tenantId }),
          });
          tenantOutcomes.push({ key: outcomeKey, tenantId, label, status: "attached" });
        } catch (attachError) {
          tenantOutcomes.push({
            key: outcomeKey,
            tenantId,
            label,
            status: "failed",
            message: attachError instanceof ApiError ? attachError.message : "Something went wrong.",
          });
        }
      }

      setResult({ lease, tenantOutcomes });
    } catch (createError) {
      // A tenant-attach failure never reaches here — this catch is only
      // reachable from the `POST /leases` call itself, so a real, specific
      // `409 duplicate_lease_start_date` (or any other creation failure)
      // is always reported honestly, never as a generic message (AC).
      setSubmitError(
        createError instanceof ApiError ? createError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDone() {
    if (result) {
      onOpenChange(false);
      onCreated(result.lease.id);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={result ? "Lease added" : "Add a lease"}>
      {result ? (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-body text-ink">
            Lease created, starting {formatDateCell(result.lease.start_date)}.
          </p>
          {result.tenantOutcomes.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {result.tenantOutcomes.map((outcome) => (
                <li key={outcome.key} className="text-body text-ink">
                  {outcome.status === "attached" ? (
                    <span>{outcome.label} — attached</span>
                  ) : (
                    <span className="text-danger">
                      {outcome.label} — not attached ({outcome.message})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          {result.tenantOutcomes.some((outcome) => outcome.status === "failed") ? (
            <p className="text-body text-ink-secondary">
              Not every tenant could be attached. The lease itself was created successfully — the
              tenants above that failed were not.
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Link
              to={`/tenants-leases/leases/${result.lease.id}`}
              className="self-center text-body text-accent underline"
            >
              View lease
            </Link>
            <Button type="button" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <PropertyUnitPicker
            autoFocus
            propertyId={cascade.propertyId}
            unitId={cascade.unitId}
            properties={cascade.properties}
            units={cascade.units}
            onPropertyChange={cascade.handlePropertyChange}
            onUnitChange={cascade.setUnitId}
          />

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{labelFor("start_date")}</span>
            <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          </label>

          <fieldset className="flex flex-col gap-1">
            <legend className="text-label font-medium text-ink-secondary">{labelFor("status")}</legend>
            <div className="flex gap-4">
              {CREATABLE_STATUSES.map((value) => (
                <label key={value} className="flex items-center gap-2 text-body text-ink">
                  <input
                    type="radio"
                    name="lease-status"
                    value={value}
                    checked={status === value}
                    onChange={() => setStatus(value)}
                    className="h-4 w-4"
                  />
                  {LEASE_STATUS_LABELS[value] ?? value}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{labelFor("end_date")}</span>
            <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{labelFor("monthly_rent_cents")}</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder="0.00"
              value={monthlyRentDollars}
              onChange={(event) => setMonthlyRentDollars(event.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{labelFor("lease_type")}</span>
            <Input value={leaseType} onChange={(event) => setLeaseType(event.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{labelFor("notice_period_days")}</span>
            <Input
              type="number"
              min="0"
              value={noticePeriodDays}
              onChange={(event) => setNoticePeriodDays(event.target.value)}
            />
          </label>

          <div className="flex flex-col gap-3">
            <span className="text-label font-medium text-ink-secondary">Tenants</span>
            {tenantSelections.map((value, index) => (
              <div key={index} className="flex items-end gap-2">
                <div className="flex-1">
                  <Combobox
                    label={`Tenant ${index + 1}`}
                    options={tenantOptions}
                    value={value}
                    onChange={(next) =>
                      setTenantSelections((existing) => existing.map((v, i) => (i === index ? next : v)))
                    }
                    placeholder="Search tenants…"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setTenantSelections((existing) => existing.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              className="self-start"
              onClick={() => setTenantSelections((existing) => [...existing, null])}
            >
              + Add a tenant
            </Button>
          </div>

          {submitError ? (
            <p role="alert" className="text-meta text-danger">
              {submitError}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? "Adding…" : "Add lease"}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
